import _get from 'lodash/get'
import { match } from 'ts-pattern'

import {
  claimSublimitFormValues,
  fetchClaimSublimits,
  previousInsurerName,
} from '@flows/configs/dental/utils/claimSublimits'
import { formatClaimSublimit } from '@flows/configs/dental/utils/format'
import { underwritingStep } from '@flows/configs/dental/utils/navigation'
import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import type { Condition } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'

import { amountRow, claimSublimitRows, unlimitedRow } from './dental_previous_insurance'

const crossedOut = {
  labelColor: 'grey500',
  valueColor: 'grey500',
  config: { boldValue: true, crossedOut: true },
} as const

const highlighted = {
  config: { boldValue: true, valueHighlight: true, valueHighlightColor: 'paleSpringLight' },
  isBold: true,
} as const

const yearsOfPreviousInsurance = (
  params: StepConfigParams,
  matches: (years: number) => boolean
) => {
  const path = params.helperFunctions.getTransientPath('years_of_previous_insurance')
  return {
    type: 'function',
    valuePaths: [path],
    function: (values) => matches(Number(_get(values, path))),
  } as const satisfies Condition
}

const yearsInfoBox = <TData>(
  params: StepConfigParams,
  matches: (years: number) => boolean,
  data: TData,
  isBoldLabel?: true
) =>
  ({
    type: 'infoBox',
    bgColor: 'grey200',
    noPadding: true,
    condition: yearsOfPreviousInsurance(params, matches),
    contentConfiguration: [
      { type: 'list', isBoldLabel, padding: 'none', paddingLeft: 'none', gapY: 'small', data },
    ],
  }) as const

const limitRow = (
  params: StepConfigParams,
  labelKey: 'standard' | 'firstYear' | 'firstTwoYears' | 'firstThreeYears',
  year: 'first' | 'second' | 'third'
) =>
  amountRow(
    params,
    `sharedFlows_dental:steps.dental_previous_reimbursement.info.limits.${labelKey}`,
    `claim_sublimit_for_${year}_year`
  )

const dental_previous_reimbursement = (params: StepConfigParams, plaform: 'native' | 'web') => {
  const nextStep = underwritingStep(plaform)
  return {
    onPrepare: {
      type: 'simpleHook',
      callback: async (flowValues) => {
        const claimSublimits = await fetchClaimSublimits(params, flowValues)
        const reimbursementAmount = match(claimSublimits?.years_of_previous_insurance)
          .with(1, () =>
            formatClaimSublimit(claimSublimits?.claim_sublimit_for_second_year, params)
          )
          .with(2, () => formatClaimSublimit(claimSublimits?.claim_sublimit_for_third_year, params))
          .otherwise(() =>
            t('sharedFlows_dental:steps.dental_previous_insurance.info.limits.unlimited')
          )

        return {
          formValues: {
            [params.helperFunctions.getTransientPath('reimbursement_amount')]: reimbursementAmount,
            [params.helperFunctions.getTransientPath('years_of_previous_insurance')]:
              claimSublimits?.years_of_previous_insurance,
            ...claimSublimitFormValues(params, claimSublimits),
            [params.helperFunctions.getTransientPath('insurer')]: previousInsurerName(
              params,
              flowValues
            ),
          },
        }
      },
    },
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_previous_coverage_breakdown.title',
        },
        size: 'h3',
      },
      // Less than a year description
      {
        type: 'text',
        emphasis: 'default',
        containsHTML: true,
        color: 'grey500',
        condition: yearsOfPreviousInsurance(params, (years) => years < 1),
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_dental:steps.dental_previous_reimbursement.description.lessThanYear',
          values: {
            insurer: {
              type: 'dynamicValue',
              path: params.helperFunctions.getTransientPath('insurer'),
            },
          },
        },
        style: 'body1',
      },
      // Over a year description
      {
        type: 'text',
        emphasis: 'default',
        containsHTML: true,
        color: 'grey500',
        condition: yearsOfPreviousInsurance(params, (years) => years >= 1),
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_previous_reimbursement.description',
          values: {
            insurer: {
              type: 'dynamicValue',
              path: params.helperFunctions.getTransientPath('insurer'),
            },
            years: {
              type: 'dynamicValue',
              path: params.helperFunctions.getTransientPath('years_of_previous_insurance'),
            },
          },
        },
        style: 'body1',
      },
      {
        type: 'title',
        color: 'grey700',
        condition: yearsOfPreviousInsurance(params, (years) => years >= 1),
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_previous_reimbursement.budgetTitle',
          values: {
            amount: {
              type: 'dynamicValue',
              path: params.helperFunctions.getTransientPath('reimbursement_amount'),
            },
          },
        },
        size: 'h4',
      },
      // Active for less than a year
      yearsInfoBox(
        params,
        (years) => years < 1,
        claimSublimitRows(params, { firstYearKey: 'claim_sublimit_for_first_year' })
      ),
      // Active for a 1 full year
      yearsInfoBox(params, (years) => years === 1, [
        { ...limitRow(params, 'standard', 'first'), ...crossedOut },
        { ...limitRow(params, 'firstYear', 'second'), ...highlighted },
        limitRow(params, 'firstTwoYears', 'third'),
        unlimitedRow,
      ]),
      // Active for 2 full years
      yearsInfoBox(
        params,
        (years) => years === 2,
        [
          { ...limitRow(params, 'standard', 'first'), ...crossedOut },
          { ...limitRow(params, 'firstTwoYears', 'second'), ...crossedOut },
          { ...limitRow(params, 'firstYear', 'third'), ...highlighted },
          unlimitedRow,
        ],
        true
      ),
      // Active for 3 plus years
      yearsInfoBox(
        params,
        (years) => years >= 3,
        [
          { ...limitRow(params, 'standard', 'first'), ...crossedOut },
          { ...limitRow(params, 'firstTwoYears', 'second'), ...crossedOut },
          { ...limitRow(params, 'firstThreeYears', 'third'), ...crossedOut },
          { ...unlimitedRow, ...highlighted },
        ],
        true
      ),
    ],
    subFlow: params.subFlow,
    nextStep,
  } as const satisfies AnyStepConfiguration
}

export default dental_previous_reimbursement
